import { PaperClipIcon, XMarkIcon } from "@heroicons/react/24/outline";
import * as Popover from "@radix-ui/react-popover";
import { observer } from "mobx-react";
import { useState } from "react";
import { Globe01Icon, ImageIcon, PlayIcon } from "../../icon-imports";
import Asset from "../../models/Asset";
import FileExtension, { getColorForExtension } from "../FileExtension";
import Tooltip from "../Tooltip";

type CompactAttachmentBarProps = {
  assets: Asset[];
  onRemove: (assetId: string) => void;
};

const MAX_VISIBLE = 2;

type AttachmentKind = "youtube" | "webpage" | "image" | "file";

const getAssetUrl = (asset: Asset): string | undefined => {
  const url = asset.content?.url;
  if (typeof url === "string" && url.startsWith("http")) return url;
  const name = asset.content?.name;
  if (typeof name === "string" && name.startsWith("http")) return name;
  return undefined;
};

const getAttachmentKind = (asset: Asset): AttachmentKind => {
  if (asset.contentType === "youtube") return "youtube";
  const url = getAssetUrl(asset);
  if (url) {
    try {
      const hostname = new URL(url).hostname;
      if (hostname.includes("youtube.com") || hostname.includes("youtu.be")) {
        return "youtube";
      }
    } catch {
      // fall through to webpage
    }
    return "webpage";
  }
  if (asset.contentType?.startsWith("image/")) return "image";
  return "file";
};

const iconBadgeClasses =
  "flex items-center justify-center w-5 h-5 rounded-md shrink-0";

const SiteFavicon = ({ url }: { url: string }) => {
  const [failed, setFailed] = useState(false);
  let hostname: string | undefined;
  try {
    hostname = new URL(url).hostname;
  } catch {
    hostname = undefined;
  }

  if (!hostname || failed) {
    return (
      <span className={`${iconBadgeClasses} bg-blue-500`}>
        <Globe01Icon className="w-3 h-3 text-white" />
      </span>
    );
  }

  return (
    <img
      src={`https://www.google.com/s2/favicons?domain=${hostname}&sz=64`}
      onError={() => setFailed(true)}
      className="w-5 h-5 rounded-md shrink-0"
      alt=""
    />
  );
};

const AttachmentIcon = ({ asset }: { asset: Asset }) => {
  const kind = getAttachmentKind(asset);

  if (kind === "youtube") {
    return (
      <span className={`${iconBadgeClasses} bg-red-600`}>
        <PlayIcon className="w-3 h-3 text-white" />
      </span>
    );
  }

  if (kind === "webpage") {
    return <SiteFavicon url={getAssetUrl(asset) as string} />;
  }

  if (kind === "image") {
    return (
      <span className={`${iconBadgeClasses} bg-purple-500`}>
        <ImageIcon className="w-3 h-3 text-white" />
      </span>
    );
  }

  const name = asset.content?.name || "";
  const parts = name.split(".");
  const extension = parts.length > 1 ? parts.pop() : undefined;

  if (!extension) {
    return (
      <span className={`${iconBadgeClasses} bg-gray-400`}>
        <PaperClipIcon className="w-3 h-3 text-white" />
      </span>
    );
  }

  return (
    <span
      className={`${iconBadgeClasses} text-[8px] font-bold uppercase text-white`}
      style={{ background: getColorForExtension(extension) }}
    >
      {extension.slice(0, 4)}
    </span>
  );
};

// Get a short display name for the attachment
const getShortName = (asset: Asset) => {
  const maxLength = 20;
  const kind = getAttachmentKind(asset);

  // Links: prefer the page/video title once processing has filled it in,
  // otherwise fall back to the domain.
  if (kind === "youtube" || kind === "webpage") {
    const title = asset.content?.title;
    if (typeof title === "string" && title.trim()) {
      return title.length > maxLength
        ? title.slice(0, maxLength) + "..."
        : title;
    }
    const url = getAssetUrl(asset);
    if (url) {
      try {
        return new URL(url).hostname.replace("www.", "");
      } catch {
        return url.length > maxLength ? url.slice(0, maxLength) + "..." : url;
      }
    }
  }

  const name = asset.content?.name || asset.content?.url || "File";

  // For files, show name with extension
  if (name.length > maxLength) {
    const parts = name.split(".");
    const ext = parts.length > 1 ? "." + parts.pop() : "";
    const baseName = parts.join(".");
    const availableLength = maxLength - ext.length - 3;
    return baseName.slice(0, availableLength) + "..." + ext;
  }

  return name;
};

function CompactAttachmentBar({ assets, onRemove }: CompactAttachmentBarProps) {
  const [isOpen, setIsOpen] = useState(false);

  if (!assets || assets.length === 0) {
    return null;
  }

  const visibleAssets = assets.slice(0, MAX_VISIBLE);
  const hiddenAssets = assets.slice(MAX_VISIBLE);
  const hiddenCount = hiddenAssets.length;
  const hasMore = hiddenCount > 0;

  // Get full name for tooltip
  const getFullName = (asset: Asset) => {
    return asset.content?.name || asset.content?.url || "File";
  };

  return (
    <div className="flex items-center gap-2 flex-wrap">
      {/* Visible file chips */}
      {visibleAssets.map((asset) => (
        <Tooltip key={asset.id} content={getFullName(asset)} sideOffset={8}>
          <div className="flex items-center gap-2 pl-2 pr-3 py-1.5 bg-white border border-gray-200 hover:border-gray-300 shadow-sm rounded-lg text-sm text-gray-700 font-medium group transition-colors cursor-default">
            <AttachmentIcon asset={asset} />
            <span className="truncate max-w-[140px]">
              {getShortName(asset)}
            </span>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onRemove(asset.id);
              }}
              className="ml-1 p-0.5 rounded-full hover:bg-gray-300 opacity-70 group-hover:opacity-100 transition-opacity"
            >
              <XMarkIcon className="w-4 h-4" />
            </button>
          </div>
        </Tooltip>
      ))}

      {/* "+X more" button with popover */}
      {hasMore && (
        <Popover.Root open={isOpen} onOpenChange={setIsOpen}>
          <Popover.Trigger asChild>
            <button
              type="button"
              className="px-3 py-1.5 bg-blue-50 hover:bg-blue-100 rounded-lg text-sm text-blue-600 font-medium transition-colors"
              onClick={(e) => e.stopPropagation()}
            >
              +{hiddenCount} more
            </button>
          </Popover.Trigger>

          <Popover.Portal>
            <Popover.Content
              className="z-[1005] w-72 bg-white rounded-lg shadow-lg border border-gray-200 p-2 animate-in fade-in-0 zoom-in-95"
              sideOffset={8}
              align="start"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="text-xs font-medium text-gray-500 px-2 py-1 mb-1">
                {hiddenCount} more {hiddenCount === 1 ? "file" : "files"}
              </div>
              <div className="max-h-48 overflow-y-auto space-y-1">
                {hiddenAssets.map((asset) => (
                  <div
                    key={asset.id}
                    className="flex items-center justify-between p-2 rounded-md hover:bg-gray-50 group"
                  >
                    <Tooltip
                      content={getFullName(asset)}
                      side="left"
                      sideOffset={8}
                    >
                      <div className="flex items-center gap-2 min-w-0 flex-1 cursor-default">
                        <FileExtension title={asset.content?.name || "file"} />
                        <div className="min-w-0 flex-1">
                          <div className="text-sm text-gray-700 font-medium truncate">
                            {asset.content?.name ||
                              asset.content?.url ||
                              "Untitled"}
                          </div>
                          {asset.size && (
                            <div className="text-xs text-gray-400">
                              {formatBytes(asset.size)}
                            </div>
                          )}
                        </div>
                      </div>
                    </Tooltip>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onRemove(asset.id);
                        // Close popover if this was the last hidden item
                        if (hiddenAssets.length <= 1) {
                          setIsOpen(false);
                        }
                      }}
                      className="p-1 rounded hover:bg-red-50 text-gray-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all"
                    >
                      <XMarkIcon className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
              <Popover.Arrow className="fill-white" />
            </Popover.Content>
          </Popover.Portal>
        </Popover.Root>
      )}

      {/* Show "View all" when we have exactly MAX_VISIBLE or more and want to show popover option */}
      {assets.length >= 2 && !hasMore && (
        <Popover.Root open={isOpen} onOpenChange={setIsOpen}>
          <Popover.Trigger asChild>
            <button
              type="button"
              className="px-2 py-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg text-sm font-medium transition-colors"
              onClick={(e) => e.stopPropagation()}
            >
              •••
            </button>
          </Popover.Trigger>

          <Popover.Portal>
            <Popover.Content
              className="z-[1005] w-72 bg-white rounded-lg shadow-lg border border-gray-200 p-2 animate-in fade-in-0 zoom-in-95"
              sideOffset={8}
              align="start"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="text-xs font-medium text-gray-500 px-2 py-1 mb-1">
                {assets.length} files attached
              </div>
              <div className="max-h-48 overflow-y-auto space-y-1">
                {assets.map((asset) => (
                  <div
                    key={asset.id}
                    className="flex items-center justify-between p-2 rounded-md hover:bg-gray-50 group"
                  >
                    <Tooltip
                      content={getFullName(asset)}
                      side="left"
                      sideOffset={8}
                    >
                      <div className="flex items-center gap-2 min-w-0 flex-1 cursor-default">
                        <FileExtension title={asset.content?.name || "file"} />
                        <div className="min-w-0 flex-1">
                          <div className="text-sm text-gray-700 font-medium truncate">
                            {asset.content?.name ||
                              asset.content?.url ||
                              "Untitled"}
                          </div>
                          {asset.size && (
                            <div className="text-xs text-gray-400">
                              {formatBytes(asset.size)}
                            </div>
                          )}
                        </div>
                      </div>
                    </Tooltip>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onRemove(asset.id);
                        if (assets.length <= 1) {
                          setIsOpen(false);
                        }
                      }}
                      className="p-1 rounded hover:bg-red-50 text-gray-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all"
                    >
                      <XMarkIcon className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
              <Popover.Arrow className="fill-white" />
            </Popover.Content>
          </Popover.Portal>
        </Popover.Root>
      )}
    </div>
  );
}

// Helper to format bytes
function formatBytes(bytes: number | undefined): string {
  if (!bytes) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i];
}

export default observer(CompactAttachmentBar);
